import React, { useState, useEffect } from "react";
import { useGuitarMatch } from "../../context/GuitarMatchContext";
import { UserManual } from "./UserManual";
import { AISeverityConfig } from "./AISeverityConfig";
import { DiagnosticPanel } from "./DiagnosticPanel";
import { SessionHistory } from "../SessionHistory";
import { ChallengeArena } from "../auth/ChallengeArena";
import { LeagueHub } from "../auth/LeagueHub";
import { RewardGallery } from "../auth/RewardGallery";
import { AppState } from "../../types";
import { registerURLManual } from "../../services/memoryService";
import { getSessionAudio } from "../../services/storageService";

interface HelpHubProps {
  initialTab?: string;
}

export const HelpHub: React.FC<HelpHubProps> = ({ initialTab = "MANUAL" }) => {
  const {
    setShowSupportHub,
    setResult,
    setAppState,
    userProfile,
    lang,
    setTargetBpm,
    setCurrentLevelId,
  } = useGuitarMatch();

  const [activeTab, setActiveTab] = useState<string>(initialTab);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  const tabs = [
    { id: "MANUAL", label: "Guide", icon: "📖" },
    { id: "IA", label: "Coach", icon: "🔬" },
    { id: "ARENA", label: "Arena", icon: "⚔️" },
    { id: "LEAGUE", label: "League", icon: "🥇" },
    { id: "REWARDS", label: "Trophées", icon: "🎁" },
    { id: "HISTORY", label: "Archives", icon: "📂" },
    { id: "DIAG", label: "Debug", icon: "⚙️" },
  ];

  const handleLoadSession = async (session: any) => {
    // Restauration du contexte de la session archivée
    if (session?.bpm) setTargetBpm(session.bpm);

    // ⚠️ Le champ peut varier selon les versions :
    // - session.result.coaching_level
    // - session.result.currentLevelId
    const lvl =
      session?.result?.coaching_level ??
      session?.result?.currentLevelId ??
      session?.coaching_level ??
      null;

    if (typeof lvl === "number") setCurrentLevelId(lvl);

    // ✅ IMPORTANT : les blob: URLs ne sont pas persistants entre reload.
    // On reconstitue alignedUrls.stu depuis IndexedDB STORE_AUDIO.
    const sessionId = session?.id;
    let stuUrl = "";

    try {
      if (typeof sessionId === "number") {
        const stuFile = await getSessionAudio(sessionId);
        stuUrl = stuFile
          ? registerURLManual(URL.createObjectURL(stuFile), `history_${sessionId}`)
          : "";
      }
    } catch {
      stuUrl = "";
    }

    // ✅ refUrl (si jamais il existe dans l’archive) — sinon vide
    const refUrl =
      session?.result?.alignedUrls?.ref ||
      (session?.result as any)?.alignedUrls?.ref ||
      "";

    const patchedResult = {
      ...(session?.result || {}),
      alignedUrls: {
        ref: refUrl,
        stu: stuUrl,
      },
    };

    setResult(patchedResult);
    setAppState(AppState.RESULTS);
    setShowSupportHub(false);
  };

  return (
    <div className="fixed inset-0 z-[15000] flex justify-end">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-xl"
        onClick={() => setShowSupportHub(false)}
      />

      <div className="relative w-full max-w-4xl bg-[#020d0f] border-l border-white/10 h-full shadow-2xl flex flex-col animate-fade-in-left overflow-hidden">
        <div className="p-10 bg-gradient-to-br from-[#001D26] to-black border-b border-white/5 shrink-0">
          <div className="flex justify-between items-start mb-10">
            <div className="space-y-1">
              <h2 className="text-4xl font-black text-white italic tracking-tighter uppercase leading-none">
                Studio <br />
                <span className="text-cyan-400">Master Hub</span>
              </h2>
              <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.4em] mt-2">
                Bienvenue, {userProfile?.pseudo || "—"}
              </p>
            </div>

            <button
              onClick={() => setShowSupportHub(false)}
              className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center text-2xl hover:bg-white/10 transition-all border border-white/10 shadow-xl"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="flex gap-2 bg-black/40 p-1.5 rounded-[30px] border border-white/5 w-fit overflow-x-auto no-scrollbar max-w-full">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-6 py-3 rounded-[24px] text-[10px] font-black uppercase transition-all flex items-center gap-3 whitespace-nowrap ${
                  activeTab === t.id
                    ? "bg-white text-black shadow-lg"
                    : "text-slate-500 hover:text-white"
                }`}
              >
                <span>{t.icon}</span> <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-10 custom-scrollbar bg-black/20">
          {activeTab === "MANUAL" && <UserManual />}
          {activeTab === "IA" && <AISeverityConfig />}
          {activeTab === "ARENA" && (
            <ChallengeArena challenges={[]} onlineMembers={[]} onJoin={() => {}} lang={lang} />
          )}
          {activeTab === "LEAGUE" && <LeagueHub onSelectChallenge={() => {}} />}
          {activeTab === "REWARDS" && <RewardGallery profile={userProfile as any} />}

          {activeTab === "HISTORY" && (
            <div className="h-full relative overflow-hidden rounded-[40px]">
              <SessionHistory
                isOpen={true}
                onClose={() => {}}
                onLoadSession={handleLoadSession}
              />
            </div>
          )}

          {activeTab === "DIAG" && <DiagnosticPanel />}
        </div>

        <div className="p-8 bg-black border-t border-white/5 text-center shrink-0">
          <p className="text-[8px] font-black text-slate-700 uppercase tracking-[0.6em]">
            GSC Digital Lab • Global Ecosystem v2.0
          </p>
        </div>
      </div>
    </div>
  );
};